import { setAnalyticsIdentity, clearAnalyticsIdentity } from '@playcastdotio/Analytics';
import { authState, guestAttributionState, hostMatchmakingState, loginProfileState } from '../stores/shared';
import { getElectronStores } from '../stores/electronStores';
import { getPersistentPlayerId } from '../utilities/persistentId';

/**
 * Initialize Analytics Identity for Host Frontend (Electron)
 *
 * Sets up automatic population of analytics events with the correct IDs based on:
 * - Guest mode: Uses ownerAuthId from guestAttributionState (real owner, not "anon")
 * - Standard mode: Uses hostAuthId from authState
 *
 * This ensures guest mode analytics are attributed to the correct owner.
 */

const listeners = new Map<string, string>();

export const initializeAnalyticsIdentity = () => {
  const electronStores = getElectronStores();

  const updateHostIdentity = () => {
    const isGuestMode = hostMatchmakingState.currentState.guestAccount;

    // In guest mode, use the owner's auth ID from guestAttributionState (Clerk auth ID, never anonymous)
    // In standard mode, use the regular hostAuthId from authState (Clerk auth ID)
    let hostAuthId: string;
    
    if (isGuestMode) {
      // Guest mode: Only use owner's Clerk auth ID from guestAttributionState
      // Never use authState.hostAuthId in guest mode (it could be an anonymous ID)
      hostAuthId = guestAttributionState.currentState.ownerAuthId || '';
      
      if (!hostAuthId) {
        console.warn('[Analytics Identity] Guest mode but no ownerAuthId available - analytics will not include hostAuthId');
      }
    } else {
      // Standard mode: Use hostAuthId from authState (should be Clerk auth ID)
      hostAuthId = authState.currentState.hostAuthId || '';
    }
    
    // Safety check: reject anonymous IDs in both guest and standard mode
    // This ensures we never use anonymous IDs, even if they somehow get into the state
    if (hostAuthId && hostAuthId.startsWith('anon_')) {
      const mode = isGuestMode ? 'guest' : 'standard';
      console.warn(`[Analytics Identity] Rejected anonymous hostAuthId in ${mode} mode - this should not happen`);
      hostAuthId = '';
    }

    const machineId = electronStores.machineIdState.currentState;
    const persistentId = getPersistentPlayerId();

    // setAnalyticsIdentity handles change detection automatically (only updates if values changed)
    // It also validates that hostAuthId is not anonymous (additional safety layer)
    setAnalyticsIdentity('host', {
      hostAuthId,
      machineId,
      persistentId,
    });
  };

  // Set up listeners for all relevant state changes
  listeners.set('authState', authState.addListener(updateHostIdentity));
  listeners.set('guestAttributionState', guestAttributionState.addListener(updateHostIdentity));
  listeners.set('machineIdState', electronStores.machineIdState.addListener(updateHostIdentity));
  listeners.set('hostMatchmakingState', hostMatchmakingState.addListener(updateHostIdentity));

  // Initialize immediately
  updateHostIdentity();

  console.log('[Analytics Identity] Initialized (Electron) - analytics events will auto-populate IDs from identity store');
};

/**
 * Initialize Analytics Identity for Remote Dashboard (Web)
 *
 * Sets up automatic population of analytics events for remote dashboard context.
 * Only populates hostAuthId and persistentId (no machineId in web context).
 */
export const initializeAnalyticsIdentityRemote = () => {
  const updateHostIdentity = () => {
    let hostAuthId = loginProfileState.currentState.hostAuthId || '';
    
    // Safety check: reject anonymous IDs
    if (hostAuthId && hostAuthId.startsWith('anon_')) {
      console.warn('[Analytics Identity] Rejected anonymous hostAuthId in remote dashboard - this should not happen');
      hostAuthId = '';
    }
    
    const persistentId = getPersistentPlayerId();

    // setAnalyticsIdentity handles change detection automatically (only updates if values changed)
    // It also validates that hostAuthId is not anonymous (additional safety layer)
    setAnalyticsIdentity('host', {
      hostAuthId,
      persistentId,
      // No machineId in remote dashboard context
    });
  };

  // Set up listener for login profile state changes
  listeners.set('loginProfileState', loginProfileState.addListener(updateHostIdentity));

  // Initialize immediately
  updateHostIdentity();

  console.log('[Analytics Identity] Initialized (Remote) - analytics events will auto-populate IDs from identity store');
};

export const cleanupAnalyticsIdentity = () => {
  console.log('[Analytics Identity] Cleaning up listeners');

  // Remove all listeners
  const electronStores = getElectronStores();
  if(listeners.get('authState')) {
    authState.removeListener(listeners.get('authState'));
  }
  if(listeners.get('guestAttributionState')) {
    guestAttributionState.removeListener(listeners.get('guestAttributionState'));
  }
  if(listeners.get('machineIdState')) {
    electronStores.machineIdState.removeListener(listeners.get('machineIdState'));
  }
  if(listeners.get('hostMatchmakingState')) {
    hostMatchmakingState.removeListener(listeners.get('hostMatchmakingState'));
  }
  if(listeners.get('loginProfileState')) {
    loginProfileState.removeListener(listeners.get('loginProfileState'));
  }

  listeners.clear();

  // Clear the identity store
  clearAnalyticsIdentity('host');
};

